import { useLayoutEffect, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import * as commonActions from '../../../actions/commonActions'
import { getSeries, createSeries, updateSeries } from '../../../actions/seriesActions'
import { resetState, setField, seriesFormData, selectAllCourses, selectAllStudents } from '../../../reducers/seriesReducer'
import * as constants from '../../../misc/Constants'

import SeriesDetails from './Steps/SeriesDetails'
import EventSchedule from './Steps/EventSchedule'
import ManageSeriesRoster from './Steps/ManageSeriesRoster'

import { ReactComponent as DetailsIcon } from '../../../assets/images/common/icons/programBuilder/details-icon.svg'
import { ReactComponent as ScheduleIcon } from '../../../assets/images/common/icons/programBuilder/schedule-icon.svg'
import { ReactComponent as MarketingIcon } from '../../../assets/images/common/icons/programBuilder/marketing-icon.svg'

import FlowHeader from '../../common/FlowHeader/FlowHeader'
import FlowNavigation from '../../common/FlowNavigation/FlowNavigation'
import FlowFooter from '../../common/FlowFooter/FlowFooter'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import SaveModal from '../../common/SaveModal/SaveModal'

import styles from './EventSeries.module.scss'

const customErrorMessages = {
  title: 'Please enter the title.',
  reminderDescription: 'Please provide a description.',
}

// eslint-disable-next-line complexity
const EventSeries = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()

  const { id, step, status, errors } = useSelector(state => state.series)
  const seriesFields = useSelector(state => state.series)
  const formData = useSelector(seriesFormData)
  const courses = useSelector(selectAllCourses)
  const students = useSelector(selectAllStudents)

  const [loader, setLoader] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [seriesLoading, setSeriesLoading] = useState(!!params.id)
  const dataLoaded = seriesLoading
  const isPublished = (id && status !== 'draft')

  useLayoutEffect(() => {
    dispatch(commonActions.showSideBar(false))
    dispatch(commonActions.showNavBar(false))
  }, [dispatch])

  useEffect(() => {
    const paramsStep = searchParams.get('step')
    if (paramsStep && ['1', '2', '3', '4'].includes(paramsStep)) {
      dispatch(setField({ field: 'step', value: parseInt(paramsStep) }))
    }
  }, [dispatch, searchParams])

  useEffect(() => {
    if (params.id) {
      dispatch(getSeries(params.id)).unwrap().then(() => setSeriesLoading(false))
    }
  }, [dispatch, params.id])

  const invalidFields = {
    title: seriesFields.title === '',
    reminderDescription: (seriesFields.earlyReminderDays || seriesFields.lateReminderDays) && seriesFields.reminderDescription === '',
  }

  const validateSeries = (isDraft) => {
    const errors = {};

    ['title', 'reminderDescription'].forEach((type) => {
      if (invalidFields[type]) {
        errors[type] = customErrorMessages[type]
        errors.step1 = true
      }
    })

    if (!isDraft && !seriesFields.events?.length ) {
      errors.step2 = true
    }

    if (!isDraft && !students.length && !courses.length) {
      errors.step3 = true
    }

    dispatch(setField({ field: 'errors', value: errors }))

    return Object.keys(errors).length === 0
  }

  const handleCreateSeries = (seriesData) => {
    setLoader(true)

    dispatch(createSeries(seriesData)).unwrap()
      .then(() => {
        dispatch(resetState())
        navigate('/events')
      })
      .catch(() => {
        toast.error('Something went wrong. Please check the steps where errors occurred and fix them.', { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const handleUpdateSeries = (seriesData) => {
    setLoader(true)

    dispatch(updateSeries(seriesData)).unwrap()
      .then(() => {
        dispatch(resetState())
        navigate('/events')
      })
      .catch(() => {
        toast.error('Something went wrong. Please check the steps where errors occurred and fix them.', { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const handleSaveSeries = (saveAsDraft) => {
    if (validateSeries(saveAsDraft)) {
      const seriesData = { ...formData, status: saveAsDraft ? 'draft' : 'active' }
      if (id) {
        seriesData.id = id
        handleUpdateSeries(seriesData)
      } else {
        handleCreateSeries(seriesData)
      }
    } else {
      toast.error(
        'Oops, it seems you are missing some required fields. Please check the steps where errors occurred and fix them.',
        { position: toast.POSITION.TOP_RIGHT }
      )
    }
  }

  const handleSave = () => {
    isPublished ? handleSaveSeries(false) : setShowSaveModal(true)
  }

  const steps = [
    { index: 1, title: 'Series Details', icon: DetailsIcon },
    { index: 2, title: 'Event Schedule', icon: ScheduleIcon },
    { index: 3, title: 'Manage Roster', icon: MarketingIcon },
  ]

  return (
    <div className={styles['event-series']}>
      {dataLoaded
        ? <Loader message='Loading series' />
        : <>
          <div className='sticky-top'>
            <FlowHeader
              isPublished={isPublished}
              onSave={handleSaveSeries}
              setShowSaveModal={setShowSaveModal}
              loader={loader}
              title={'9 Hole Events'}
            />
            <FlowNavigation
              currentStep={step}
              steps={steps}
              errors={errors}
            />
          </div>
          <div className={styles.contentContainer}>
            {
              step === 1 && <SeriesDetails/> ||
              step === 2 && <EventSchedule/> ||
              step === 3 && <ManageSeriesRoster/>
            }
          </div>
        </>
      }
      <FlowFooter
        isPublished={isPublished}
        handleSaveClick={handleSave}
        loader={loader}
        dataLoaded={dataLoaded}
        step={step}
        steps={steps}
      />

      <SaveModal
        showModal={showSaveModal}
        closeModal={() => setShowSaveModal(false)}
        publish={() => { handleSaveSeries(false); setShowSaveModal(false) }}
        draft={() => { handleSaveSeries(true); setShowSaveModal(false) }}
        title={'Publish Event Series'}
        message={constants.SERIES_SAVE_MESSAGE}
      />
    </div>
  )
}

export default EventSeries
